import { useState, useMemo, useRef, useEffect } from 'react';

import { UI_TEXT, DOM_CONSTANTS, SELECTION_TYPES } from './constants';
import styles from './MultiSelectDropDown.module.css';
import { SelectionType, MultiSelectDropDownOption, SelectedItem } from './types';
import SearchBar from './SearchBar';
import DropDownResults from './DropDownResults';
import SelectedItemsList from './SelectedItemsList';
import NoResults from './NoResults';
import { useDropdownPosition } from './hooks/useDropdownPosition';
import { useKeyboardNavigation } from '../hooks/useKeyboardNavigation';
import { useClickOutside } from '../hooks/useClickOutside';
import { useItemSelection } from './hooks/useItemSelection';
import { filterOptions, flattenOptions } from './utils/filterOptions';

interface MultiSelectDropDownProps {
  options: MultiSelectDropDownOption[] | Record<string, MultiSelectDropDownOption[]>;
  placeholder?: string;
  onSelectionChange?: (selectedItems: SelectedItem[]) => void;
  label: string;
}

const MultiSelectDropDown: React.FC<MultiSelectDropDownProps> = ({
  options,
  placeholder = UI_TEXT.DEFAULT_PLACEHOLDER,
  onSelectionChange,
  label,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isMapOptions = !Array.isArray(options);

  const filteredOptions = useMemo(
    () => filterOptions(options, searchTerm, isMapOptions),
    [options, searchTerm, isMapOptions],
  );

  const flattenedOptions = useMemo(
    () => flattenOptions(filteredOptions, isMapOptions),
    [filteredOptions, isMapOptions],
  );

  const { selectedItems, handleItemSelect, removeItem } = useItemSelection(onSelectionChange);

  const { dropdownPosition, dropdownHorizontalPosition, calculateDropdownPosition } =
    useDropdownPosition(containerRef, dropdownRef);

  const handleClose = () => {
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const handleKeyboardItemSelect = (item: MultiSelectDropDownOption) => {
    const existingItem = selectedItems.find((selected) => selected.value === item.id);
    if (!existingItem) {
      // Not selected, include it
      handleItemSelect(item.id, undefined, SELECTION_TYPES.INCLUDE, item.value);
    } else if (existingItem.type === SELECTION_TYPES.INCLUDE) {
      // Currently included, exclude it
      handleItemSelect(item.id, undefined, SELECTION_TYPES.EXCLUDE, item.value);
    } else if (existingItem.type === SELECTION_TYPES.EXCLUDE) {
      // Currently excluded, remove it
      removeItem(item.id);
    }
  };

  const { focusedIndex, setFocusedIndex } = useKeyboardNavigation({
    isOpen,
    items: flattenedOptions,
    onItemSelect: handleKeyboardItemSelect,
    onClose: handleClose,
    containerRef: inputRef,
    hierarchical: false,
  });

  useClickOutside(containerRef, isOpen, handleClose);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(calculateDropdownPosition, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, calculateDropdownPosition]);

  const toggleDropdown = () => {
    if (!isOpen) {
      calculateDropdownPosition();
      setFocusedIndex(-1);
    }
    setIsOpen(!isOpen);
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setFocusedIndex(-1);
  };

  const handleFocus = () => {
    setIsOpen(true);
    calculateDropdownPosition();
    setFocusedIndex(-1);
  };

  return (
    <div className={styles.container} ref={containerRef} data-testid="multiselect-container">
      <label className={styles.label}>{label}</label>
      <SearchBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        placeholder={placeholder}
        onFocus={handleFocus}
        isOpen={isOpen}
        onToggle={toggleDropdown}
        inputRef={inputRef}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls="dropdown-results"
        aria-activedescendant={focusedIndex >= 0 ? `option-${focusedIndex}` : undefined}
      />

      {isOpen &&
        (flattenedOptions.length > 0 ? (
          <DropDownResults
            ref={dropdownRef}
            options={filteredOptions}
            selectedItems={selectedItems}
            onItemSelect={handleItemSelect}
            isMapOptions={isMapOptions}
            verticalPosition={dropdownPosition}
            horizontalPosition={dropdownHorizontalPosition}
            focusedIndex={focusedIndex}
            id="dropdown-results"
            role="listbox"
          />
        ) : (
          <NoResults />
        ))}

      <SelectedItemsList selectedItems={selectedItems} onRemoveItem={removeItem} />
    </div>
  );
};

export default MultiSelectDropDown;
